import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";

import MagneticButton from "@/components/MagneticButton";
import { SERVICES, PAGE_CONTENT } from "@/lib/constants";

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      {/* header */}
      <section className="pt-40 pb-20 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="font-display text-[11px] uppercase tracking-[0.4em] text-copper mb-5">{PAGE_CONTENT.services.hero.kicker}</div>
          <h1 className="font-serif text-6xl md:text-8xl lg:text-[10rem] leading-[0.9] tracking-tighter">
            {PAGE_CONTENT.services.hero.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.services.hero.title2}</em>
          </h1>
          <p className="mt-10 max-w-2xl text-lg text-parchment/70 leading-relaxed">
            {PAGE_CONTENT.services.hero.desc}
          </p>
        </motion.div>

        <div className="mt-14 flex flex-wrap gap-3">
          {SERVICES.map((s) => (
            <a
              key={s.slug}
              href={`#${s.slug}`}
              data-testid={`services-chip-${s.slug}`}
              className="px-4 py-2 border border-line hover:border-copper text-[11px] uppercase tracking-[0.28em] font-display text-parchment/80 hover:text-copper transition-colors"
            >
              {s.num} · {s.title}
            </a>
          ))}
        </div>
      </section>

      {/* services detail */}
      {SERVICES.map((s, idx) => {
        const reverse = idx % 2 !== 0;
        return (
          <section
            key={s.slug}
            id={s.slug}
            className="relative border-t border-line scroll-mt-28"
            data-testid={`service-section-${s.slug}`}
          >
            <div className={`max-w-7xl mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9 }}
                className="lg:col-span-6"
              >
                <div className="aspect-[4/5] overflow-hidden border border-line">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover kenburns-img" />
                </div>
              </motion.div>

              <div className="lg:col-span-6">
                <div className="font-display text-[11px] uppercase tracking-[0.4em] text-copper mb-4">
                  {s.num} · {s.kicker}
                </div>
                <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight mb-6">
                  {s.title.split(" ").slice(0, -1).join(" ")}{" "}
                  <em className="italic font-light text-copper">{s.title.split(" ").slice(-1)}</em>
                </h2>
                <p className="font-serif italic text-xl text-parchment/80 mb-8 leading-snug border-l border-copper pl-5">
                  {s.tagline}
                </p>
                <p className="text-parchment/70 mb-10 leading-relaxed">{s.short}</p>

                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mb-10">
                  {s.features.map((f, i) => (
                    <div key={i} className="flex gap-3">
                      <Check className="mt-1 text-copper shrink-0" size={16} />
                      <div>
                        <div className="font-serif text-lg leading-snug">{f.t}</div>
                        <div className="text-sm text-parchment/60 leading-relaxed">{f.d}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
                  {s.specs.map((sp, i) => (
                    <div key={i} className="p-4 bg-surface border border-line">
                      <div className="font-display text-[10px] uppercase tracking-[0.25em] text-copper mb-1">{sp.k}</div>
                      <div className="font-serif text-xl leading-tight">{sp.v}</div>
                    </div>
                  ))}
                </div>

                <div className="mb-10">
                  <div className="font-display text-[10px] uppercase tracking-[0.3em] text-muted2 mb-3">Perfect For</div>
                  <div className="flex flex-wrap gap-2">
                    {s.perfectFor.map((p) => (
                      <span key={p} className="px-3 py-1.5 text-[11px] font-display uppercase tracking-[0.2em] border border-line text-parchment/80">{p}</span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <MagneticButton as="a" href="/contact" testId={`book-${s.slug}`}>
                    Book This Service <ArrowUpRight size={14} />
                  </MagneticButton>
                  <MagneticButton as="a" href="/contact" variant="ghost" testId={`quote-${s.slug}`}>
                    Get a Quote
                  </MagneticButton>
                  {s.demoLink && (
                    <MagneticButton as="a" href={s.demoLink} testId={`demo-${s.slug}`} className="bg-transparent border border-parchment/30 text-parchment hover:bg-parchment hover:text-ink flex items-center gap-2">
                      View Demo <ArrowUpRight size={14} />
                    </MagneticButton>
                  )}
                </div>
              </div>
            </div>
          </section>
        );
      })}



      <div className="max-w-4xl mx-auto text-center px-6 py-24">
        <h3 className="font-serif text-4xl md:text-5xl leading-tight mb-8">{PAGE_CONTENT.services.cta.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.services.cta.title2}</em></h3>
        <Link to="/contact">
          <MagneticButton as="a" href="/contact" testId="services-final-cta">Start Your Booking <ArrowUpRight size={14} /></MagneticButton>
        </Link>
      </div>
    </div>
  );
}
